import React, { useState, useEffect } from 'react';
import './adminBlog.css';
import TiptapEditor from '../ui/TiptapEditor/TiptapEditor';
import { useBlog } from '../../hooks/useBlog';
import useImageUpload from '../../hooks/useImageUpload';
import Modal from '../common/Modal/modal';
import { Trash2, Edit, Plus, X, ImagePlus } from 'lucide-react';

const getBlogTitle = (blog) => blog?.title || blog?.Title || '';
const getBlogAuthor = (blog) => blog?.author || blog?.Author || '';
const getBlogThumbnail = (blog) => blog?.thumbnail || blog?.Thumbnail || '';
const getBlogTags = (blog) => blog?.tags || blog?.Tags || [];
const getBlogCreatedAt = (blog) => {
    const createdAt = blog?.createdAt || blog?.CreatedAt;

    if (!createdAt) {
        return 'N/A';
    }

    const parsedDate = new Date(createdAt);
    return Number.isNaN(parsedDate.getTime()) ? 'N/A' : parsedDate.toISOString();
};

function AdminBlog({ idToken }) {
    const { blogs, loadingBlogs, error, fetchBlogs, addBlog, updateBlog, deleteBlog } = useBlog();
    const { handleImageUpload, uploading } = useImageUpload();

    const [selectedBlogForEdit, setSelectedBlogForEdit] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');
    const [tags, setTags] = useState('');
    const [content, setContent] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [thumbnailUrl, setThumbnailUrl] = useState('');
    const [formError, setFormError] = useState('');

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs]);

    useEffect(() => {
        if (selectedBlogForEdit) {
            setTitle(getBlogTitle(selectedBlogForEdit));
            setAuthor(getBlogAuthor(selectedBlogForEdit));
            setTags(getBlogTags(selectedBlogForEdit).join('; ') || '');
            setContent(selectedBlogForEdit.content || selectedBlogForEdit.Content || '');
            setExcerpt(selectedBlogForEdit.excerpt || selectedBlogForEdit.Excerpt || '');
            setThumbnailUrl(getBlogThumbnail(selectedBlogForEdit));
            setShowForm(true);
        }
    }, [selectedBlogForEdit]);

    const resetForm = () => {
        setTitle('');
        setAuthor('');
        setTags('');
        setContent('');
        setExcerpt('');
        setThumbnailUrl('');
        setSelectedBlogForEdit(null);
        setShowForm(false);
        setFormError('');
    };

    const handleThumbnailUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const url = await handleImageUpload(file, idToken);
            if (url) setThumbnailUrl(url);
        } catch (err) {
            setFormError(err.message || 'Erreur lors de l upload de la miniature');
        }
    };

    const removeThumbnail = () => {
        setThumbnailUrl('');
    };

    const handleChangeContent = (html) => {
        setContent(html);
        const plainText = html.replace(/<[^>]+>/g, '');
        setExcerpt(plainText.slice(0, 150) + (plainText.length > 150 ? '...' : ''));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        if (!content || content === '<p></p>') {
            setFormError('Le contenu ne peut pas être vide');
            return;
        }
        if (!thumbnailUrl) {
            setFormError('Une miniature est requise');
            return;
        }

        const now = new Date();
        const blogData = {
            title,
            slug: title
                .toLowerCase()
                .replace(/[^\w\s-]/g, '')
                .trim()
                .replace(/\s+/g, '-'),
            subtitle: `${author} - ${now.toLocaleDateString()}`,
            content,
            excerpt,
            createdAt: selectedBlogForEdit?.createdAt || now.toISOString(),
            updatedAt: now.toISOString(),
            author,
            tags: tags.split(';').map((t) => t.trim()).filter(Boolean),
            thumbnail: thumbnailUrl,
        };

        try {
            if (selectedBlogForEdit) {
                const success = await updateBlog(selectedBlogForEdit.id, blogData, idToken);
                if (success) {
                    alert('Article modifié avec succès !');
                    resetForm();
                }
            } else {
                const result = await addBlog(blogData, idToken);
                if (result) {
                    alert('Article enregistré avec succès !');
                    resetForm();
                }
            }
        } catch (err) {
            setFormError(err.message || 'Erreur lors de l enregistrement');
        }
    };

    const handleEdit = (blog) => {
        setSelectedBlogForEdit(blog);
    };

    const handleDelete = async () => {
        if (!deleteConfirmId) return;
        try {
            const success = await deleteBlog(deleteConfirmId, idToken);
            if (success) {
                alert('Article supprimé avec succès');
                setDeleteConfirmId(null);
            }
        } catch (err) {
            setFormError(err.message || 'Erreur lors de la suppression');
            setDeleteConfirmId(null);
        }
    };

    if (loadingBlogs) {
        return <div className="admin-blog-container"><div className="admin-loading">Chargement des articles...</div></div>;
    }

    return (
        <div className="admin-blog-container">
            <div className="admin-blog-list-section">
                <div className="admin-blog-list-header">
                    <h2>Articles du blog</h2>
                    <button className="admin-add-btn" onClick={() => { resetForm(); setShowForm(true); }}>
                        <Plus size={20} /> Nouvel article
                    </button>
                </div>

                {error && <div className="admin-error">{error}</div>}

                <div className="admin-blog-grid">
                    {blogs.length === 0 ? (
                        <div className="no-results">Aucun article trouvé</div>
                    ) : (
                        blogs.map((blog) => (
                            <div key={blog.id} className="blog-card">
                                <div className="blog-card-image">
                                    {getBlogThumbnail(blog) ? <img src={getBlogThumbnail(blog)} alt={getBlogTitle(blog)} /> : <div className="no-image"><ImagePlus size={48} /></div>}
                                </div>
                                <div className="blog-card-info">
                                    <h4>{getBlogTitle(blog)}</h4>
                                    <p className="blog-card-meta">
                                        {getBlogAuthor(blog)} — {getBlogCreatedAt(blog)}
                                    </p>
                                    {getBlogTags(blog).length > 0 && (
                                        <div className="blog-card-tags">
                                            {getBlogTags(blog).map((tag, idx) => (
                                                <span key={idx} className="tag-tag">{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="blog-card-actions">
                                        <button className="action-btn edit-btn" onClick={() => handleEdit(blog)} title="Modifier"><Edit size={18} /></button>
                                        <button className="action-btn delete-btn" onClick={() => setDeleteConfirmId(blog.id)} title="Supprimer"><Trash2 size={18} /></button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <Modal isOpen={showForm} onClose={resetForm}>
                <div className="admin-form-card admin-blog-modal-card">
                    <div className="form-header">
                        <h3>{selectedBlogForEdit ? 'Modifier l article' : 'Nouvel article'}</h3>
                    </div>

                    <form onSubmit={handleSubmit} className="admin-blog-form admin-blog-modal-body">
                        {formError && <div className="admin-error">{formError}</div>}
                        <label className="admin-blog-label">
                            Titre
                            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
                        </label>
                        <label className="admin-blog-label">
                            Auteur
                            <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)} required />
                        </label>
                        <label className="admin-blog-label">
                            Tags (séparés par des points-virgules)
                            <input type="text" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="fashion; news" />
                        </label>
                        <label className="admin-blog-label">
                            Miniature
                            <input type="file" accept="image/*" onChange={handleThumbnailUpload} disabled={uploading} />
                        </label>
                        {thumbnailUrl && (
                            <div className="thumbnail-preview thumbnail-preview--blog">
                                <img src={thumbnailUrl} alt="Miniature" />
                                <button type="button" className="remove-thumbnail-btn" onClick={removeThumbnail}><X size={16} /></button>
                            </div>
                        )}
                        <label className="admin-blog-label admin-blog-editor-label">
                            Contenu
                            <TiptapEditor value={content} onChange={handleChangeContent} placeholder="Écrivez votre article ici..." />
                        </label>
                        <button type="submit" className="admin-submit-btn" disabled={uploading}>{selectedBlogForEdit ? 'Mettre à jour' : 'Créer'}</button>
                    </form>
                </div>
            </Modal>

            {deleteConfirmId && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal">
                        <p>Voulez-vous vraiment supprimer cet article ?</p>
                        <div className="admin-modal-actions">
                            <button onClick={() => setDeleteConfirmId(null)}>Annuler</button>
                            <button className="danger" onClick={handleDelete}>Supprimer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminBlog;
